import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuthContext } from "@/contexts/AuthContext";

const publicLinks = [
  { label: "Ana Sayfa", path: "/" },
  { label: "Fiyatlandırma", path: "/pricing" },
];

const authLinks = [
  { label: "QR Oluşturucu", path: "/generator" },
  { label: "QR Kodlarım", path: "/my-qr" },
  { label: "İstatistikler", path: "/statistics" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const location = useLocation();
  const { user, signOut } = useAuthContext();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setProfileOpen(false);
  }, [location.pathname]);

  const isActive = (path: string) => location.pathname === path;

  const navLinks = user ? [...publicLinks, ...authLinks] : publicLinks;

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled ? "bg-white/95 backdrop-blur-md border-b border-gray-200/80" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 cursor-pointer">
          <img
            src="https://public.readdy.ai/ai/img_res/5f29dc48-9590-4b13-ae7e-f0353c3fcc43.png"
            alt="QRGo Logo"
            className="h-9 w-auto object-contain"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isActive(link.path)
                  ? "text-blue-600 bg-blue-50"
                  : scrolled
                  ? "text-slate-600 hover:text-blue-600 hover:bg-slate-50"
                  : "text-slate-700 hover:text-blue-600 hover:bg-white/20"
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <div className="relative">
              <button
                onClick={() => setProfileOpen(!profileOpen)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer"
              >
                <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-bold">
                  {user.fullName.charAt(0).toUpperCase()}
                </div>
                <span className="text-sm font-medium text-slate-700">{user.fullName}</span>
                <i className={`ri-arrow-down-s-line text-slate-400 transition-transform ${profileOpen ? "rotate-180" : ""}`}></i>
              </button>

              {profileOpen && (
                <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl border border-gray-100 shadow-lg overflow-hidden py-1">
                  <Link
                    to="/settings"
                    className="flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors cursor-pointer"
                  >
                    <i className="ri-user-settings-line text-slate-400"></i>
                    Hesap Ayarları
                  </Link>
                  <button
                    onClick={async () => {
                      try {
                        await signOut();
                      } catch (error) {
                        console.error("Sign out error:", error);
                      }
                      setProfileOpen(false);
                    }}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                  >
                    <i className="ri-logout-box-r-line"></i>
                    Çıkış Yap
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <Link
                to="/login"
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Giriş Yap
              </Link>
              <Link
                to="/register"
                className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-full transition-all whitespace-nowrap cursor-pointer"
              >
                Kayıt Ol
              </Link>
            </>
          )}
        </div>

        <button
          className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg text-slate-700 cursor-pointer"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <i className={`text-xl ${menuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
        </button>
      </div>

      {menuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 px-6 py-4 flex flex-col gap-2">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              onClick={() => setMenuOpen(false)}
              className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                isActive(link.path)
                  ? "text-blue-600 bg-blue-50"
                  : "text-slate-600 hover:text-blue-600 hover:bg-slate-50"
              }`}
            >
              {link.label}
            </Link>
          ))}
          {user ? (
            <>
              <Link
                to="/settings"
                onClick={() => setMenuOpen(false)}
                className="px-4 py-2.5 rounded-lg text-sm font-medium text-slate-600 hover:text-blue-600 hover:bg-slate-50 transition-colors cursor-pointer"
              >
                Hesap Ayarları
              </Link>
              <button
                onClick={async () => {
                  try {
                    await signOut();
                  } catch (error) {
                    console.error("Sign out error:", error);
                  }
                  setMenuOpen(false);
                }}
                className="px-4 py-2.5 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 transition-colors cursor-pointer text-left"
              >
                Çıkış Yap
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                onClick={() => setMenuOpen(false)}
                className="px-4 py-2.5 rounded-lg text-sm font-medium text-slate-600 hover:text-blue-600 hover:bg-slate-50 transition-colors cursor-pointer"
              >
                Giriş Yap
              </Link>
              <Link
                to="/register"
                onClick={() => setMenuOpen(false)}
                className="mt-2 px-5 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-full text-center cursor-pointer"
              >
                Kayıt Ol
              </Link>
            </>
          )}
        </div>
      )}
    </header>
  );
}
